import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { invokeEdgeFunction } from "@/lib/admin-auth";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  RefreshCw,
  RotateCcw,
  Loader2,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { PLAN_LABELS } from "@/lib/order-inquiry";

type Booking = {
  id: string;
  plan: string;
  customer_email: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  status: string;
  amount_cents: number;
  currency: string;
  stripe_session_id: string | null;
  notes: string | null;
  inquiry_details: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  paid: "bg-green-100 text-green-800",
  interested: "bg-blue-100 text-blue-800",
  refunded: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800",
};

const STATUS_TABS = [
  "all",
  "pending",
  "paid",
  "interested",
  "refunded",
  "cancelled",
] as const;

const PAGE_SIZE = 10;

function formatCurrency(cents: number, currency: string) {
  const value = cents / 100;
  const symbol = currency?.toUpperCase() === "EUR" ? "€" : currency?.toUpperCase() === "USD" ? "$" : currency?.toUpperCase() === "GBP" ? "£" : `${currency} `;
  return `${symbol}${value.toFixed(2)}`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function exportCSV(bookings: Booking[]) {
  const headers = [
    "ID",
    "Date",
    "Customer Name",
    "Customer Email",
    "Customer Phone",
    "Plan",
    "Amount",
    "Currency",
    "Status",
    "Stripe Session ID",
    "Notes",
    "Created At",
    "Updated At",
  ];

  const rows = bookings.map((b) => [
    b.id,
    formatDate(b.created_at),
    b.customer_name ?? "",
    b.customer_email ?? "",
    b.customer_phone ?? "",
    b.plan,
    (b.amount_cents / 100).toFixed(2),
    b.currency,
    b.status,
    b.stripe_session_id ?? "",
    b.notes ?? "",
    b.created_at,
    b.updated_at,
  ]);

  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const csv = [headers.map(escape).join(","), ...rows.map((r) => r.map(escape).join(","))].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `bookings-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function TableSkeleton() {
  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
      <div className="bg-gray-50 border-b px-4 py-3 flex gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-4 w-24" />
        ))}
      </div>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="px-4 py-3 flex gap-4 border-b last:border-0">
          {Array.from({ length: 6 }).map((_, j) => (
            <Skeleton key={j} className="h-4 w-24" />
          ))}
        </div>
      ))}
    </div>
  );
}

export default function Bookings() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [refunding, setRefunding] = useState<string | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setBookings(data || []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const filtered = useMemo(() => {
    let result = bookings;

    if (statusFilter !== "all") {
      result = result.filter((b) => b.status === statusFilter);
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (b) =>
          (b.customer_name?.toLowerCase().includes(q) ?? false) ||
          (b.customer_email?.toLowerCase().includes(q) ?? false)
      );
    }

    return result;
  }, [bookings, statusFilter, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, searchQuery]);

  const handleRefund = async (id: string) => {
    setRefunding(id);
    try {
      await invokeEdgeFunction("refund-payment", { bookingId: id });
      toast.success("Refund processed successfully");
      await fetchBookings();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Refund failed");
    } finally {
      setRefunding(null);
    }
  };

  const openDetail = (booking: Booking) => {
    setSelectedBooking(booking);
    setDrawerOpen(true);
  };

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: bookings.length };
    for (const b of bookings) {
      counts[b.status] = (counts[b.status] || 0) + 1;
    }
    return counts;
  }, [bookings]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Bookings</h2>
          <p className="text-sm text-gray-500 mt-1">
            {filtered.length} booking{filtered.length !== 1 ? "s" : ""}{" "}
            {statusFilter !== "all" && `· ${statusFilter}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => exportCSV(filtered)}
            className="gap-1.5"
            disabled={loading || filtered.length === 0}
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchBookings}
            className="gap-1.5"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by customer name or email…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
        />
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === s
                ? "bg-primary text-white shadow-sm"
                : "bg-white border text-gray-600 hover:bg-gray-50"
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
            <span className="ml-1.5 text-xs opacity-75">
              {statusCounts[s] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <TableSkeleton />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-lg font-medium">No bookings found</p>
          <p className="text-sm mt-1">
            {searchQuery
              ? "Try adjusting your search query"
              : "No bookings match the current filter"}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">
                    Date
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">
                    Customer
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">
                    Plan
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">
                    Amount
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">
                    Status
                  </th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paginated.map((b) => (
                  <tr
                    key={b.id}
                    onClick={() => openDetail(b)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {formatDate(b.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 truncate max-w-[200px]">
                        {b.customer_name || "—"}
                      </div>
                      <div className="text-xs text-gray-500 truncate max-w-[200px]">
                        {b.customer_email || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {PLAN_LABELS[b.plan] || b.plan}
                    </td>
                    <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                      {formatCurrency(b.amount_cents, b.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                          STATUS_COLORS[b.status] || "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {b.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {b.status === "paid" && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                              disabled={refunding === b.id}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {refunding === b.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <RotateCcw className="w-3 h-3" />
                              )}
                              Refund
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Confirm Refund
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to refund{" "}
                                <strong>
                                  {formatCurrency(b.amount_cents, b.currency)}
                                </strong>{" "}
                                to{" "}
                                <strong>
                                  {b.customer_name || b.customer_email || "this customer"}
                                </strong>
                                ? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleRefund(b.id)}
                                className="bg-red-600 hover:bg-red-700 text-white"
                              >
                                Yes, Refund
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Showing {(safePage - 1) * PAGE_SIZE + 1}–
                {Math.min(safePage * PAGE_SIZE, filtered.length)} of{" "}
                {filtered.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </Button>
                <span className="text-sm text-gray-600 min-w-[80px] text-center">
                  Page {safePage} of {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="gap-1"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Booking detail drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Booking Details</SheetTitle>
          </SheetHeader>

          {selectedBooking && (
            <div className="mt-6 space-y-6">
              {/* Status badge */}
              <div className="flex items-center gap-3">
                <span
                  className={`inline-block px-3 py-1 rounded-full text-sm font-medium capitalize ${
                    STATUS_COLORS[selectedBooking.status] ||
                    "bg-gray-100 text-gray-800"
                  }`}
                >
                  {selectedBooking.status}
                </span>
                <span className="text-sm text-gray-500">
                  {selectedBooking.id}
                </span>
              </div>

              {/* Customer info */}
              <section>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  Customer
                </h4>
                <dl className="space-y-2">
                  <DetailRow label="Name" value={selectedBooking.customer_name} />
                  <DetailRow label="Email" value={selectedBooking.customer_email} />
                  <DetailRow label="Phone" value={selectedBooking.customer_phone} />
                </dl>
              </section>

              {/* Booking info */}
              <section>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  Booking
                </h4>
                <dl className="space-y-2">
                  <DetailRow label="Plan" value={PLAN_LABELS[selectedBooking.plan] || selectedBooking.plan} />
                  <DetailRow
                    label="Amount"
                    value={formatCurrency(
                      selectedBooking.amount_cents,
                      selectedBooking.currency
                    )}
                  />
                  <DetailRow
                    label="Currency"
                    value={selectedBooking.currency?.toUpperCase()}
                  />
                </dl>
              </section>

              {/* Payment info */}
              <section>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  Payment
                </h4>
                <dl className="space-y-2">
                  <DetailRow
                    label="Stripe Session"
                    value={selectedBooking.stripe_session_id}
                    mono
                  />
                </dl>
              </section>

              {/* Market research / inquiry details */}
              {selectedBooking.inquiry_details &&
                Object.keys(selectedBooking.inquiry_details).length > 0 && (
                <section>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Parent & care needs
                  </h4>
                  <dl className="space-y-2">
                    <DetailRow label="Parent name" value={String(selectedBooking.inquiry_details.parentName || "—")} />
                    <DetailRow label="Parent age" value={String(selectedBooking.inquiry_details.parentAge || "—")} />
                    <DetailRow label="Country" value={String(selectedBooking.inquiry_details.parentCountry || "—")} />
                    <DetailRow label="City" value={String(selectedBooking.inquiry_details.parentCity || "—")} />
                    <DetailRow label="Living situation" value={String(selectedBooking.inquiry_details.livingSituation || "—")} />
                    <DetailRow
                      label="Care needs"
                      value={
                        Array.isArray(selectedBooking.inquiry_details.careNeeds)
                          ? (selectedBooking.inquiry_details.careNeeds as string[]).join(", ")
                          : "—"
                      }
                    />
                    <DetailRow label="Other needs" value={String(selectedBooking.inquiry_details.careNeedsOther || "—")} />
                    <DetailRow label="Timeline" value={String(selectedBooking.inquiry_details.timeline || "—")} />
                    <DetailRow label="Preferred contact" value={String(selectedBooking.inquiry_details.preferredContact || "—")} />
                  </dl>
                </section>
              )}

              {/* Notes */}
              {selectedBooking.notes && (
                <section>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Notes
                  </h4>
                  <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">
                    {selectedBooking.notes}
                  </p>
                </section>
              )}

              {/* Timestamps */}
              <section>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  Timestamps
                </h4>
                <dl className="space-y-2">
                  <DetailRow
                    label="Created"
                    value={formatDateTime(selectedBooking.created_at)}
                  />
                  <DetailRow
                    label="Updated"
                    value={formatDateTime(selectedBooking.updated_at)}
                  />
                </dl>
              </section>

              {/* Refund action inside drawer */}
              {selectedBooking.status === "paid" && (
                <div className="pt-4 border-t">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        disabled={refunding === selectedBooking.id}
                      >
                        {refunding === selectedBooking.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RotateCcw className="w-4 h-4" />
                        )}
                        Refund This Booking
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Refund</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to refund{" "}
                          <strong>
                            {formatCurrency(
                              selectedBooking.amount_cents,
                              selectedBooking.currency
                            )}
                          </strong>{" "}
                          to{" "}
                          <strong>
                            {selectedBooking.customer_name ||
                              selectedBooking.customer_email ||
                              "this customer"}
                          </strong>
                          ? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleRefund(selectedBooking.id)}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          Yes, Refund
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-sm text-gray-500 shrink-0">{label}</dt>
      <dd
        className={`text-sm text-gray-900 text-right break-all ${
          mono ? "font-mono text-xs" : ""
        }`}
      >
        {value || "—"}
      </dd>
    </div>
  );
}
